// Auth-server module — returnTo validation (the open-redirect surface).
//
// WHY THIS IS ITS OWN PURE MODULE: every value here is attacker-supplied. A
// sign-in carries `callbackURL`, an OAuth leg carries `return_to`, and after the
// terms gate we hand one of them straight to `window.location.href`. Getting this
// wrong turns login.vrs.racing into an open redirector, which is a phishing
// primitive AND (because the OAuth authorize leg round-trips through here) a way
// to bounce an authorization code at an attacker. So the decision is a pure
// function of (url, allowlist) with no I/O, unit-tested in isolation, and the
// rest of the module NEVER hand-rolls its own check.
//
// WHY NOT REUSE Better Auth's `trustedOrigins`: that list drives the CSRF/origin
// check on every auth endpoint. vrs-one is a DESKTOP client whose OAuth callback
// listens on an ephemeral loopback port, so allowing it means allowing
// `http://localhost:*` — and putting that in trustedOrigins would let ANY page
// served from any localhost port drive the auth endpoints with the member's
// cookies. Redirect-target trust and request-origin trust are different
// questions with different blast radii, so they get different lists. See
// betterAuth.js trustedOrigins for the other one.

// The default when nothing usable was supplied. Relative on purpose: it is
// correct on every host the auth server answers on.
export const DEFAULT_RETURN_TO = '/account'

// The absolute-URL allowlist. Relative paths do not consult this at all.
//
//   *.vrs.racing         the site, the auth host, and the GWT WebApp (app.).
//   loopback wildcards   vrs-one. The port CANNOT be pinned: the desktop client
//                        binds an ephemeral port and registers it per-launch, so
//                        a fixed port would break the client on every restart.
//                        Scoped to the loopback names ONLY — never a wildcard
//                        host — so this cannot be widened into `http://*`.
//   virtualracingschool  the legacy WebApp redirect target, live until the OAuth
//                        clients finish moving to *.vrs.racing.
//
// Frozen: this is a security boundary, not a tuning knob. Changing it is a code
// change that shows up in review, not a config edit that shows up in an outage.
export const DEFAULT_RETURN_TO_ORIGINS = Object.freeze([
  'https://vrs.racing',
  'https://www.vrs.racing',
  'https://login.vrs.racing',
  'https://app.vrs.racing',
  // Both the wildcard AND the bare form: `http://localhost:*` cannot match an
  // origin that has no port at all (`http://localhost`, i.e. port 80), because
  // the wildcard stands in for the port and not for the colon that introduces
  // it. Listing both is clearer than making the colon optional inside the
  // pattern language, which would blur where a wildcard may appear.
  'http://localhost:*',
  'http://localhost',
  'http://127.0.0.1:*',
  'http://127.0.0.1',
  'https://virtualracingschool.appspot.com',
  'https://login.virtualracingschool.com',
])

// A RELATIVE path we are willing to redirect to.
//
// Every clause is load-bearing:
//   ^\/                 must be site-relative.
//   (?!\/)              rejects `//evil.com`, which browsers treat as a
//                       PROTOCOL-RELATIVE absolute URL, not a path.
//   no backslash        browsers normalise `\` to `/`, so `/\evil.com` is
//                       `//evil.com` by the time it is followed.
//   no %2f / %5c        the percent-encoded forms of the two above; some
//                       proxies and frameworks decode before redirecting.
//   printable ASCII     a raw CR/LF would split the Location header, and a tab
//                       is stripped by browsers inside a URL (`/\t/evil.com`).
//
// Deliberately checked as explicit rejections rather than one dense regex: each
// bypass class stays greppable and gets its own named test.
function isSafeRelativePath(url) {
  if (typeof url !== 'string' || url.length === 0 || url.length > 2048) return false
  if (url[0] !== '/') return false
  if (url[1] === '/') return false
  if (url.includes('\\')) return false
  const lower = url.toLowerCase()
  // Only the leading segment matters for the encoded-slash bypass, but an
  // encoded backslash anywhere in the PATH can still be decoded into one.
  if (lower.startsWith('/%2f') || lower.startsWith('/%5c')) return false
  for (let i = 0; i < url.length; i++) {
    const c = url.charCodeAt(i)
    if (c < 0x21 || c > 0x7e) return false
  }
  return true
}

// originMatches — one allowlist entry against one concrete origin.
//
// `*` is allowed ONLY as a whole-component wildcard and the match is anchored at
// both ends, so `https://app.vrs.racing` cannot be satisfied by
// `https://app.vrs.racing.evil.com` and `http://localhost:*` cannot be satisfied
// by `http://localhost.evil.com`.
function originMatches(origin, pattern) {
  if (typeof pattern !== 'string' || !pattern) return false
  if (!pattern.includes('*')) return origin === pattern
  // Split on the wildcard FIRST, then escape each literal segment. Doing it in
  // that order means no placeholder character is needed to protect the `*` from
  // the escaper — and therefore no sentinel that could collide with input.
  // `[^/:]*` keeps a wildcard confined to one origin component, so
  // `http://localhost:*` cannot stretch across the `/` into a path or across the
  // `:` into a different host.
  const source = '^' + pattern.split('*').map(escapeRegExp).join('[^/:]*') + '$'
  return new RegExp(source).test(origin)
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// isAllowedReturnTo — the whole decision.
//
// Relative paths are allowed on their own merits (see isSafeRelativePath) and
// never consult the origin allowlist: they cannot leave the current origin, and
// which origin that is depends on the host the request arrived on.
export function isAllowedReturnTo(url, allowlist = DEFAULT_RETURN_TO_ORIGINS) {
  if (typeof url !== 'string' || !url) return false
  if (url.startsWith('/')) return isSafeRelativePath(url)

  let parsed
  try {
    parsed = new URL(url)
  } catch {
    return false
  }
  // Anything that is not plain web navigation — javascript:, data:, file:, and
  // the custom schemes a desktop client might register — is refused outright.
  // vrs-one comes back through a loopback HTTP listener, not a scheme handler.
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return false
  // `https://app.vrs.racing@evil.com/` has origin `https://evil.com`, so URL's
  // own parse already defeats the userinfo trick — but reject credentials
  // explicitly so the intent is not merely implied by a library's behaviour.
  if (parsed.username || parsed.password) return false

  for (const pattern of allowlist) {
    if (originMatches(parsed.origin, pattern)) return true
  }
  return false
}

// sanitizeReturnTo — isAllowedReturnTo with a safe default, for the call sites
// that must produce SOME destination. Never throws, never returns an untrusted
// value, and never returns undefined.
export function sanitizeReturnTo(url, allowlist = DEFAULT_RETURN_TO_ORIGINS, fallback = DEFAULT_RETURN_TO) {
  return isAllowedReturnTo(url, allowlist) ? url : fallback
}

// buildTermsRedirect — the /accept-terms URL carrying an already-sanitised
// destination. The caller MUST pass a value that survived sanitizeReturnTo;
// encoding happens here so no call site can forget it.
//
// Encoding matters beyond tidiness: an OAuth `return_to` is itself a URL with a
// query string, and leaving its `&` raw would truncate it at the first
// parameter — which is how the client's redirect_uri silently goes missing.
export function buildTermsRedirect(returnTo, path = '/accept-terms') {
  return `${path}?return_to=${encodeURIComponent(returnTo)}`
}
